import {
  PREVIEW_FRESHNESS,
  type RunFeedPreviewResponse,
  type RunFeedPreviewFreshness,
} from "@nova-ai/shared"

const NONCURRENT_PREVIEW_POLL_MS = 5_000

type InspectionPage = Pick<RunFeedPreviewResponse, "freshness" | "inspectionIdentity">

/** Every loaded page must be current before inspection can become current. */
export function previewFreshness(
  pages: readonly InspectionPage[]
): RunFeedPreviewFreshness | undefined {
  const historical = pages.filter((page) => page.freshness.kind === PREVIEW_FRESHNESS.historical)
  const first = historical[0]?.freshness
  if (first?.kind !== PREVIEW_FRESHNESS.historical) {
    const noncurrent = pages.find((page) => page.freshness.kind !== PREVIEW_FRESHNESS.current)
    if (noncurrent) {
      return noncurrent.freshness
    }
    const identities = new Set(pages.flatMap((page) => page.inspectionIdentity ?? []))
    if (identities.size > 1) {
      return {
        kind: PREVIEW_FRESHNESS.unknown,
        reason: "inspection_inputs_changed",
        message:
          "Run inputs changed between pages. Refresh the preview before using current counts.",
      }
    }
    return pages[0]?.freshness
  }
  const unsafeProducts = historical.flatMap((page) =>
    page.freshness.kind === PREVIEW_FRESHNESS.historical ? [page.freshness.unsafeProducts] : []
  )
  return {
    ...first,
    unsafeProducts: {
      items: unsafeProducts.flatMap((products) => products.items),
      total: unsafeProducts.reduce((total, products) => total + products.total, 0),
      truncated: unsafeProducts.some((products) => products.truncated),
    },
  }
}

export function previewPollInterval(pages: readonly InspectionPage[]): number | false {
  const freshness = previewFreshness(pages)
  return freshness && freshness.kind !== PREVIEW_FRESHNESS.current
    ? NONCURRENT_PREVIEW_POLL_MS
    : false
}

/** Apply the run's freshness fence before any view adds fallback rows or drawer enrichment. */
export function restrictedPreviewData<Data extends { pages: RunFeedPreviewResponse[] }>(
  data: Data | undefined,
  freshness: RunFeedPreviewFreshness | undefined
) {
  if (
    !data ||
    !freshness ||
    freshness.kind === PREVIEW_FRESHNESS.current ||
    previewFreshness(data.pages)?.kind === freshness.kind
  ) {
    return data
  }
  return {
    ...data,
    pages: data.pages.map((page, index) => ({
      ...page,
      // Keep aggregate freshness metadata once, even with several cached pages.
      freshness: index === 0 ? freshness : page.freshness,
      buildBlocked: true,
    })),
  }
}
